import { useEffect, useRef } from "react";

/**
 * Gate tooltip pointer-events based on pointer-idle state.
 *
 * Idea: while user drags/moves fast, tooltip must NOT capture pointer events
 * (otherwise it blocks mouseover on next x-tick + brush interaction).
 * Once pointer slows / pauses for `idleDelayMs`, flip the tooltip root to
 * pointer-events:auto so users can hover items / click pin button.
 *
 * Mechanism:
 *   - Listen to pointermove on the chart wrapper, compute velocity (px/ms).
 *   - Fast movement OR active drag (buttons!=0) => mark "moving".
 *   - On idle (no fast move for delay) => mark "idle".
 *   - Toggle data-idle on the tooltip DOM root (looked up by chart uid).
 *   - CSS reads [data-idle="1"] to switch pointer-events.
 *
 * Returns nothing; purely side-effectful.
 */
export interface UseTooltipIdleGateParams {
  wrapRef: React.RefObject<HTMLElement>;
  getChartUid: () => string;
  /** px/ms velocity above this counts as "moving fast". */
  velocityThreshold?: number;
  /** Idle dwell before tooltip becomes interactive. */
  idleDelayMs?: number;
}

export function useTooltipIdleGate({
  wrapRef,
  getChartUid,
  velocityThreshold = 0.25,
  idleDelayMs = 150,
}: UseTooltipIdleGateParams) {
  const idleRef = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    let lastX = 0;
    let lastY = 0;
    let lastT = 0;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const findTooltipRoots = (): HTMLElement[] => {
      const uid = getChartUid();
      const out: HTMLElement[] = [];
      if (uid) {
        document
          .querySelectorAll(
            `.remix-tt .enhanced-tooltip[data-chart-uid="${uid}"]`,
          )
          .forEach((node) => {
            const root = (node as HTMLElement).closest(
              ".remix-tt",
            ) as HTMLElement | null;
            if (root) out.push(root);
          });
      }
      return out;
    };

    const isOverTooltip = (target: EventTarget | null) => {
      const el2 = target as HTMLElement | null;
      return !!el2?.closest?.(".remix-tt");
    };

    const isOverChart = (target: EventTarget | null) => {
      const el2 = target as HTMLElement | null;
      if (!el2) return false;
      return el === el2 || el.contains(el2);
    };

    const stampRoots = (idle: boolean) => {
      const roots = findTooltipRoots();
      roots.forEach((r) => {
        if (r.getAttribute("data-idle") !== (idle ? "1" : "0")) {
          r.setAttribute("data-idle", idle ? "1" : "0");
        }
      });
    };

    const applyIdle = (idle: boolean) => {
      const changed = idleRef.current !== idle;
      idleRef.current = idle;
      // Always stamp — tooltip root can be recreated on notMerge updates,
      // so missing attribute would silently leave it click-through.
      if (changed) stampRoots(idle);
      else stampRoots(idle);
    };

    const scheduleIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        idleTimer = null;
        applyIdle(true);
      }, idleDelayMs);
    };

    // Use document-level pointermove so we still get events when the cursor
    // is over the body-appended tooltip (outside the chart DOM subtree).
    const onPointerMove = (e: PointerEvent) => {
      const overTooltip = isOverTooltip(e.target);
      const overChart = isOverChart(e.target);
      if (!overTooltip && !overChart) {
        // Outside both — let any pending idle still fire; nothing to do.
        return;
      }

      if (overTooltip) {
        // Pointer on tooltip => keep interactive, cancel idle countdown.
        applyIdle(true);
        if (idleTimer) {
          clearTimeout(idleTimer);
          idleTimer = null;
        }
        lastX = e.clientX;
        lastY = e.clientY;
        lastT = performance.now();
        return;
      }

      const now = performance.now();
      const dt = now - lastT;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      lastT = now;
      if (dt <= 0) {
        scheduleIdle();
        return;
      }
      const v = Math.hypot(dx, dy) / dt;
      const dragging = e.buttons !== 0;
      if (dragging || v > velocityThreshold) {
        applyIdle(false);
      }
      scheduleIdle();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (isOverTooltip(e.target)) return; // allow clicks on pin etc.
      applyIdle(false);
    };

    document.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("pointerdown", onPointerDown, { passive: true });

    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerdown", onPointerDown);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [wrapRef, getChartUid, velocityThreshold, idleDelayMs]);
}
